import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloudSyncIcon from '@mui/icons-material/CloudSync'
import ErrorIcon from '@mui/icons-material/Error'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import PreviewIcon from '@mui/icons-material/Preview'
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import { useDealData } from 'api/aws/deals/useDealData'
import { baseUrl } from 'api/config'
import { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { computeNetSuiteBreakdown } from 'utils/netsuiteItemMapping'
import { downloadContract } from 'components/Deals/contracts/downloadContract'

interface SyncPreview {
  account: {
    name: string
    type: string
    external_id: string
    website_url?: string | null
    city?: string | null
    state?: string | null
  }
  contacts: Array<{
    name: string
    email: string | null
    role: string | null
  }>
  subscriptions: Array<{
    name: string
    service_line_code: string
    price: number
    quantity: number
    status: string
    start_date: string | null
    end_date: string | null
  }>
  metadata: {
    deal_name: string
    deal_type: string
    total: number
    monthly_total: number
    gross_profit: number
  }
}

const DealHeartbeatSync = () => {
  const { PK, SK } = useParams()
  const dealId = SK?.replace('DEAL_', '') || ''
  const { deal, organization, productRows } = useDealData(PK, SK, dealId)
  const theme = useTheme()

  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [downloadingContract, setDownloadingContract] = useState(false)
  const [preview, setPreview] = useState<SyncPreview | null>(null)
  const [result, setResult] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)

  const isSynced = deal?.heartbeat_synced === true
  const syncedAt = deal?.heartbeat_synced_at
  const heartbeatAccountId = organization?.heartbeat_account_id

  const handlePreview = useCallback(async () => {
    // Toggle: if a preview is already expanded, collapse it instead of re-fetching.
    if (preview) {
      setPreview(null)
      return
    }
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const res = await fetch(`${baseUrl}/aws/heartbeat/preview?PK=${encodeURIComponent(PK || '')}&dealSK=${encodeURIComponent(SK || '')}`)
      const data = await res.json()
      if (data.success) {
        setPreview(data.payload)
      } else {
        setError(data.error || 'Failed to generate preview')
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [PK, SK, preview])

  const handleSync = useCallback(async () => {
    setSyncing(true)
    setError(null)
    try {
      const res = await fetch(`${baseUrl}/aws/heartbeat/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ PK, dealSK: SK }),
      })
      const data = await res.json()
      if (data.success) {
        setResult(data.data)
        setPreview(null)
      } else {
        setError(data.error || 'Sync failed')
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSyncing(false)
    }
  }, [PK, SK])

  const handleDownloadContract = useCallback(async () => {
    if (!deal?.contractId) return
    setDownloadingContract(true)
    setError(null)
    try {
      const safeName = (deal?.name ?? '').replace(/[\\/:*?"<>|]/g, '_').trim()
      const filename = safeName ? `Contract - ${safeName}.pdf` : `contract_${deal.contractId}.pdf`
      await downloadContract(deal.contractId, filename)
    } catch (err: any) {
      setError(err?.message || 'Failed to download contract')
    } finally {
      setDownloadingContract(false)
    }
  }, [deal?.contractId, deal?.name])

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val)

  return (
    // Scroll container — the tab's PageContainer is overflow:hidden, so own the scroll here.
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 3 }}>
      <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <CloudSyncIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
        <Typography variant="h6" fontWeight={600}>
          Heartbeat Sync
        </Typography>
        {isSynced && (
          <Chip
            icon={<CheckCircleIcon />}
            label={`Synced${syncedAt ? ` ${new Date(syncedAt).toLocaleDateString()}` : ''}`}
            color="success"
            size="small"
            variant="outlined"
          />
        )}
        {heartbeatAccountId && (
          <Chip label={`Account #${heartbeatAccountId}`} size="small" variant="outlined" />
        )}
      </Stack>
{/* Sync instructions -- Disabled until we use the sync to heartbeat action*/}
      {/* <Typography variant="body2" color="text.secondary" mb={3}>
        Push this deal's data to Heartbeat to create or update the account, contacts, and subscription lines.
        {deal?.stage === 'Deal Won' && ' This deal auto-syncs when reaching "Deal Won" stage.'}
      </Typography> */}

      {/* NetSuite Entry Reference — how each product maps to NetSuite items + invoice lines */}
      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
        NetSuite Entry Reference
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        For each product on this deal, enter the highlighted <strong>Enter in Heartbeat</strong> line at the
        price shown — e.g. Meta Advertising ($100, 30% margin) is entered as “Paid Social Advertising” at
        $100. The NetSuite item breakdown (ad spend + management fee) is reference only.
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Product (Grobotic)</TableCell>
              <TableCell align="right">Grobotic Price</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Enter in Heartbeat</TableCell>
              <TableCell>NetSuite item breakdown (reference)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!productRows || productRows.length === 0) && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    No products on this deal.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {productRows?.map((product, i) => {
              const breakdown = computeNetSuiteBreakdown(product)
              const showLines = breakdown.matched && !breakdown.na
              return (
                <TableRow key={product.SK ?? i} sx={{ verticalAlign: 'top' }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {product.productName || '(unnamed product)'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2">{formatCurrency(breakdown.price)}</Typography>
                    {breakdown.marginPct > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {breakdown.marginPct}% margin
                      </Typography>
                    )}
                  </TableCell>
                  {/* The actionable line — exactly what the user keys into Heartbeat */}
                  <TableCell sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                    {breakdown.invoice ? (
                      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
                        <Typography variant="body2" fontWeight={700} color="primary.main">
                          {breakdown.invoice.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color="primary.main"
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {formatCurrency(breakdown.invoice.amount)}
                        </Typography>
                      </Stack>
                    ) : !breakdown.matched ? (
                      <Chip label="No mapping" size="small" color="warning" variant="outlined" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No Heartbeat entry
                      </Typography>
                    )}
                  </TableCell>
                  {/* NetSuite item split — reference only */}
                  <TableCell>
                    {showLines ? (
                      <Stack spacing={0.25}>
                        {breakdown.items.map((line, j) => (
                          <Stack key={j} direction="row" justifyContent="space-between" spacing={2}>
                            <Typography variant="caption" color="text.secondary">
                              {line.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {formatCurrency(line.amount)}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} mb={3}>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <PreviewIcon />}
          onClick={handlePreview}
          disabled={loading || syncing}
        >
          {preview ? 'Hide Preview' : 'Preview'}
        </Button>
        <Button
          variant="outlined"
          startIcon={downloadingContract ? <CircularProgress size={16} /> : <FileDownloadIcon />}
          onClick={handleDownloadContract}
          disabled={!deal?.contractId || downloadingContract || loading || syncing}
          title={deal?.contractId ? 'Download the signed PandaDoc contract as a PDF' : 'No contract attached to this deal'}
        >
          {downloadingContract ? 'Downloading…' : 'Download Contract'}
        </Button>
        {/* Disable direct sync for now while we work out some edge cases — preview is the main action users should take, and sync can be done manually from the preview panel once we're ready */}
        {/* <Button
          variant="contained"
          startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
          onClick={handleSync}
          disabled={loading || syncing}
        >
          {isSynced ? 'Re-sync' : 'Sync'} to Heartbeat
        </Button> */}
      </Stack>

      {/* Error */}
      {error && (
        <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Success result */}
      {result && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResult(null)}>
          Synced successfully — Account #{result.account_id}, {result.subscriptions_synced} subscription(s),{' '}
          {result.contacts_synced} contact(s)
        </Alert>
      )}

      {/* Preview Panel */}
      <Collapse in={!!preview}>
        {preview && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Account
            </Typography>
            <Stack direction="row" spacing={3} mb={2} flexWrap="wrap">
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body2">{preview.account.name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Type
                </Typography>
                <Typography variant="body2">{preview.account.type}</Typography>
              </Box>
              {preview.account.city && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body2">
                    {preview.account.city}, {preview.account.state}
                  </Typography>
                </Box>
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              Subscriptions ({preview.subscriptions.length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>Service Line</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Dates</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.subscriptions.map((sub, i) => (
                    <TableRow key={i}>
                      <TableCell>{sub.name}</TableCell>
                      <TableCell>
                        <Chip label={sub.service_line_code} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(sub.price)}</TableCell>
                      <TableCell>
                        <Chip
                          label={sub.status}
                          size="small"
                          color={sub.status === 'active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {sub.start_date || '—'} → {sub.end_date || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {preview.contacts.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Contacts ({preview.contacts.length})
                </Typography>
                <Stack spacing={0.5}>
                  {preview.contacts.map((c, i) => (
                    <Typography key={i} variant="body2">
                      {c.name}
                      {c.email && (
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' '}
                          — {c.email}
                        </Typography>
                      )}
                      {c.role && (
                        <Chip label={c.role} size="small" variant="outlined" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                  ))}
                </Stack>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" spacing={3}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Monthly Total
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(preview.metadata.monthly_total)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Deal Total
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(preview.metadata.total)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Gross Profit
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(preview.metadata.gross_profit)}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}
      </Collapse>
      </Box>
    </Box>
  )
}

export default DealHeartbeatSync
